import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { useGetPromoConnectTargetsQuery } from '../../../../redux/api/fymAPI';
import { platformName } from '../platformMeta';
import { SocialPlatform } from '../types';
import { ConnectTargetRow } from './ConnectTargetRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ConnectTargetModalProps {
  accountId: number;
  /** The platform whose login just finished. Undefined keeps the modal shut. */
  platform?: SocialPlatform;
  isSaving: boolean;
  closeModal: () => void;
  onSelect: (platform: SocialPlatform, externalId: string) => void;
  /** Starts the login again, which is the only cure for an expired choice. */
  onStartAgain: (platform: SocialPlatform) => void;
}

/**
 * Which page or account the posts go to.
 *
 * A Facebook or Instagram login can reach several pages, and the server parks
 * them for 15 minutes. After that the list is empty and the login has to run
 * again — the modal says so instead of showing nothing.
 */
const ConnectTargetModalComponent: React.FC<ConnectTargetModalProps> = ({
  accountId,
  platform,
  isSaving,
  closeModal,
  onSelect,
  onStartAgain,
}) => {
  const intl = useIntl();
  const [picked, setPicked] = useState<string>();

  const { data, isLoading, error, refetch } = useGetPromoConnectTargetsQuery(
    { accountId, platform: platform ?? 'facebook' },
    { skip: platform === undefined }
  );

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const confirm = useCallback(() => {
    if (platform !== undefined && picked !== undefined) {
      onSelect(platform, picked);
    }
  }, [onSelect, picked, platform]);

  const startAgain = useCallback(() => {
    if (platform !== undefined) {
      onStartAgain(platform);
    }
  }, [onStartAgain, platform]);

  const targets = useMemo(() => data?.targets ?? [], [data?.targets]);
  const rows = useMemo(
    () =>
      targets.map((target) => (
        <ConnectTargetRow
          key={target.externalId}
          target={target}
          isPicked={target.externalId === picked}
          onPick={setPicked}
        />
      )),
    [picked, targets]
  );

  const name = platform === undefined ? '' : platformName(platform);
  const isEmpty = !isLoading && error === undefined && targets.length === 0;

  return (
    <Modal isOpen={platform !== undefined} toggle={closeModal} centered className="sml-modal">
      <ModalHeader toggle={closeModal}>
        {intl.formatMessage(
          { id: 'app.sml.promote.channel.target.title', defaultMessage: 'Where should we post on {platform}?' },
          { platform: name }
        )}
      </ModalHeader>
      <ModalBody>
        {isLoading ? <LoadingView /> : null}
        {!isLoading && error !== undefined ? <ErrorView error={error} retry={retry} /> : null}
        {isEmpty ? (
          <p className={shared.planNote}>
            {intl.formatMessage({
              id: 'app.sml.promote.channel.target.expired',
              defaultMessage: 'That choice has expired. Start the login again and pick a page within 15 minutes.',
            })}
          </p>
        ) : null}
        {!isLoading && error === undefined && targets.length > 0 ? (
          <div
            className={shared.stack}
            role="radiogroup"
            aria-label={intl.formatMessage({
              id: 'app.sml.promote.channel.target.listLabel',
              defaultMessage: 'Pages we can post to',
            })}
          >
            {rows}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        {isEmpty ? (
          <SMLButton color="primary" omitCap disabled={isSaving} onClick={startAgain}>
            {intl.formatMessage({ id: 'app.sml.promote.channel.target.startAgain', defaultMessage: 'Start again' })}
          </SMLButton>
        ) : (
          <SMLButton color="primary" omitCap disabled={isSaving || picked === undefined} onClick={confirm}>
            {intl.formatMessage({ id: 'app.sml.promote.channel.target.confirm', defaultMessage: 'Confirm' })}
          </SMLButton>
        )}
      </ModalFooter>
    </Modal>
  );
};

export const ConnectTargetModal = TypedMemo(ConnectTargetModalComponent);
